import React, { useEffect, useState } from "react";
import { Form, InputGroup } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import * as apicall from "../../components/Apicall";
import Loader from "../../components/Loader";
import "./VendorForm.css";

// Price page for every service except Packers & Movers. For each service picked on the first page it shows,
// per service type, the offerings of that type (rows) against the vendor's service locations (columns), with a
// price box for each. The amount is the vendor's payout (vendor_payout_price). A box left empty is not offered.

const cellKey = (offeringId, locationId) => `${offeringId}|${locationId}`;

const VendorPayoutPricing = ({ vendorData, vendor_id, prev, onSubmit }) => {
  const isEdit = vendor_id != 0 && vendor_id != "0";
  const [grid, setGrid] = useState([]); // [{ service_id, name, types: [{ name, offerings: [{ offering_id, name, prices }] }] }]
  const [columns, setColumns] = useState([]); // locations that can be priced: [{ name, location_id }]
  const [values, setValues] = useState({});
  const [saved, setSaved] = useState({}); // cells that already had a price (edit)
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const currency = String(vendorData?.contactNumber || "").startsWith("91") ? "₹" : "د.إ";
  const services = vendorData?.services?.length
    ? vendorData.services
    : [{ id: Array.isArray(vendorData?.serviceType) ? vendorData.serviceType[0] : vendorData?.serviceType, name: vendorData?.service_name }];
  const locations = vendorData?.selectlocation || [];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await apicall.post(
          {
            vendor_id: isEdit ? vendor_id : 0,
            services: services.map((s) => s.id),
            locations: locations.map((l) => l.id),
          },
          "vendorPayoutGrid"
        );
        const data = res?.data || [];
        const cols = (res?.locations || []).filter((l) => l.location_id);
        const notes = [];

        const missing = (res?.locations || []).filter((l) => !l.location_id).map((l) => l.name);
        if (missing.length) {
          notes.push(`${missing.join(", ")}: cannot be priced because the location is not set up in service_locations.`);
        }
        const initial = {};
        const start = {};
        for (const service of data) {
          if (!service.types.length) {
            notes.push(`${service.name}: no service types or offerings are set up for this service.`);
          }
          for (const type of service.types) {
            for (const offering of type.offerings) {
              for (const col of cols) {
                const v = offering.prices?.[col.location_id];
                const key = cellKey(offering.offering_id, col.location_id);
                if (v !== null && v !== undefined) {
                  initial[key] = String(v);
                  start[key] = true;
                }
              }
            }
          }
        }
        setGrid(data);
        setColumns(cols);
        setValues(initial);
        setSaved(start);
        setWarnings(notes);
      } catch (e) {
        setWarnings([`Could not load the price list (${e?.message || "server error"}).`]);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    const items = [];
    let missing = false;

    // Every price box must be filled with a whole number above 0.
    for (const service of grid) {
      for (const type of service.types) {
        for (const offering of type.offerings) {
          for (const col of columns) {
            const key = cellKey(offering.offering_id, col.location_id);
            const raw = String(values[key] ?? "").trim();
            const amount = Number(raw);
            if (raw === "" || !Number.isInteger(amount) || amount <= 0) missing = true;
            items.push({ offering_id: offering.offering_id, location: col.location_id, payout_amount: amount });
          }
        }
      }
    }
    if (missing || !items.length) {
      setError("Please fill all the prices.");
      return;
    }
    setSaving(true);
    onSubmit({ month: 0, service_offerings_price: items });
    setTimeout(() => setSaving(false), 4000);
  };

  return (
    <div className="container vp">
      <h3 className="heading">Fill Offering Prices</h3>
      <p className="label">
        Enter the price for every offering in each location.
      </p>

      {loading ? (
        <Skeleton count={4} height={90} />
      ) : (
        <Form noValidate onSubmit={submit}>
          {warnings.length > 0 && (
            <div className="alert alert-warning" role="alert">
              <strong>Check the setup:</strong>
              <ul className="mb-0">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}

          {grid.map((service) => (
            <div key={service.service_id} className="vp-service">
              <div className="vp-service-title">{service.name}</div>
              {service.types.map((type) => (
                <div key={type.type_id || type.name} className="vp-offering">
                  {type.name && <div className="vp-offering-head">{type.name}</div>}
                  <div className="vp-scroll">
                    <table className="vp-table">
                      <thead>
                        <tr>
                          <th>Offering</th>
                          {columns.map((c) => <th key={c.location_id}>{c.name}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {type.offerings.map((offering) => (
                          <tr key={offering.offering_id}>
                            <td className="vp-loc">{offering.name}</td>
                            {columns.map((c) => {
                              const key = cellKey(offering.offering_id, c.location_id);
                              return (
                                <td key={c.location_id}>
                                  <InputGroup className="vp-input">
                                    <InputGroup.Text>{currency}</InputGroup.Text>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      step="1"
                                      placeholder="0"
                                      value={values[key] ?? ""}
                                      onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                                    />
                                  </InputGroup>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          ))}

          <div className="d-flex justify-content-center text-danger mt-2">{error}</div>

          <div className="vf-actions d-flex justify-content-center mt-4 mb-2">
            <button
              type="button"
              className="btn Button primaryColorText btn-outline-secondary me-2 no-hover-effect"
              onClick={prev}
            >
              Back
            </button>
            {grid.length > 0 && columns.length > 0 && (
              <button
                type="submit"
                className="btn Button primaryBackground text-white position-relative d-flex justify-content-center align-items-center"
              >
                {saving ? <Loader style={{ height: "30", width: "40" }} /> : (isEdit ? "Update" : "Save")}
              </button>
            )}
          </div>
        </Form>
      )}
    </div>
  );
};

export default VendorPayoutPricing;
